import { format } from 'date-fns';

import { DATE_FORMATS, type DateInput, type FormatPreset } from './formats';
import { appDateLocale, appLanguage, dateLocale } from './locales';
import { parseISOString, toDate } from './parse';
import { wallClockDate } from './wallClock';

/**
 * The presets with month names, in the order and spelling of each language. English keeps
 * the `DATE_FORMATS` pattern; another language not listed here does too.
 */
const WORDED_PRESETS: Partial<Record<FormatPreset, Record<string, string>>> = {
    displayLong: { nb: 'd. MMMM yyyy', es: "d 'de' MMMM 'de' yyyy" },
    displayWeek: { nb: 'd. MMM yyyy', es: 'd MMM yyyy' },
};

function presetPattern(preset: FormatPreset, language: string = appLanguage()): string {
    const base = language.toLowerCase().split('-')[0];
    return WORDED_PRESETS[preset]?.[base] ?? DATE_FORMATS[preset];
}

/**
 * Formats with a preset. `displayLong` and `displayWeek` name the month in the language the
 * portal is shown in, in that language's order.
 *
 * @example
 * formatDate(new Date(2024, 0, 15), 'display'); // "15/01/2024"
 * formatDate(new Date(2024, 0, 15), 'internal'); // "2024-01-15"
 * formatDate(null); // ""
 */
export function formatDate(date: DateInput, preset: FormatPreset = 'display'): string {
    return formatDateCustom(date, presetPattern(preset));
}

/**
 * Formats with a date-fns pattern (`d MMM yyyy`, not moment's `D MMM YYYY`),
 * or "" for a missing or invalid date. Month and day names are in the language the portal
 * is shown in.
 */
export function formatDateCustom(date: DateInput, formatString: string): string {
    const d = toDate(date);
    if (!d) return '';
    return format(d, formatString, { locale: appDateLocale() });
}

/**
 * The standard display day across the UI, `dd/MM/yyyy`, for a date without a time: day
 * columns, date inputs and last measurement days. A date with a time is worded
 * ({@link formatDateTimeWorded}).
 *
 * @example
 * formatDateDisplay(new Date(2025, 9, 31, 10, 44, 48)); // "31/10/2025"
 */
export function formatDateDisplay(date: DateInput): string {
    return formatDateCustom(date, DATE_FORMATS.display);
}

/** How much of the time a worded date-time shows: to the minute, or to the second. */
export type TimePrecision = 'minute' | 'second';

const TIME_PATTERNS: Record<TimePrecision, string> = {
    minute: 'HH:mm',
    second: 'HH:mm:ss',
};

/**
 * The date-fns pattern of a worded date-time in `language`, the page language by default: the
 * `displayWeek` day ("Aug 20, 2026", "20. aug. 2026", "20 ago. 2026") and a 24-hour time.
 */
export function dateTimeWordedPattern(
    precision: TimePrecision = 'second',
    language: string = appLanguage()
): string {
    return `${presetPattern('displayWeek', language)} ${TIME_PATTERNS[precision]}`;
}

/**
 * The display date-time across the UI. A date with a time of day names its month in the
 * language the portal is shown in, in that language's order, with a 24-hour time and never
 * AM/PM: "Aug 20, 2026 15:00", "20. aug. 2026 15:00", "20 ago. 2026 15:00" (a product
 * decision of 2026-10-14 for every language, which supersedes the numeric date-time of the
 * Operational Dashboard spec). A day without a time stays `dd/MM/yyyy`
 * ({@link formatDateDisplay}) in tables and day cells, or
 * `displayLong` where a page already words it.
 *
 * An instant is shown in the viewer's zone here; a site's wall clock goes to
 * {@link formatWallClockWorded}, and an instant in the site's zone to
 * `formatDateTimeInTimezone`.
 *
 * @param precision - `'minute'` for times set to the minute (bioscope placements, restarts,
 *   connections, the internal admin tables), `'second'` otherwise.
 * @param language - The language to word it in, the page language by default; a translation
 *   formatter passes the language it renders.
 *
 * @example
 * formatDateTimeWorded(new Date(2025, 9, 31, 10, 44, 48)); // "Oct 31, 2025 10:44:48"
 * formatDateTimeWorded(new Date(2025, 9, 5, 14, 30, 45), 'minute'); // "Oct 5, 2025 14:30"
 * formatDateTimeWorded(null); // ""
 */
export function formatDateTimeWorded(
    date: DateInput,
    precision: TimePrecision = 'second',
    language: string = appLanguage()
): string {
    const d = toDate(date);
    if (!d) return '';
    return format(d, dateTimeWordedPattern(precision, language), {
        locale: dateLocale(language),
    });
}

/**
 * A site's wall clock (see `wallClock.ts`) as a worded date-time, exactly as the site's clock
 * read it in every browser zone: the behaviour and environment "last measurement" times.
 *
 * @example
 * formatWallClockWorded('2025-10-05T14:30:45'); // "Oct 5, 2025 14:30:45"
 * formatWallClockWorded(''); // ""
 */
export function formatWallClockWorded(
    wallClock: string | null | undefined,
    precision: TimePrecision = 'second'
): string {
    const d = wallClockDate(wallClock);
    return d ? format(d, dateTimeWordedPattern(precision), { locale: appDateLocale() }) : '';
}

/** The local calendar day as `yyyy-MM-dd`, or "". */
export function toDateString(date: DateInput): string {
    return formatDate(date, 'internal');
}

/**
 * The local calendar day of an ISO timestamp as `yyyy-MM-dd`, or "".
 *
 * @example
 * extractDateFromISO('2024-01-15T10:30:00'); // "2024-01-15"
 */
export function extractDateFromISO(isoString: string | null | undefined): string {
    return toDateString(parseISOString(isoString));
}

/** The instant as UTC ISO 8601 ("2024-01-15T10:30:00.000Z"), or "". */
export function toISOString(date: DateInput): string {
    const d = toDate(date);
    if (!d) return '';
    return new Date(d.getTime()).toISOString();
}
